import { useCallback, useEffect, useRef, useState } from 'react'
import { PARTIAL_NOTE, WITHHELD, partialOf, sumShown } from '../api/answer'
import { Withheld, markPartial, memberName, memberText } from './Withheld'
import type { Api } from '../api'
import { ApiError, LOCAL, NO_RECV_FILTERS } from '../api/types'
import type { ApiFailure, DsoNode, RecvFilters } from '../api/types'
import { EmptyState, Loading, money } from './ui'
import { FailureState } from './FailureState'
import { ExportButtons } from './ExportButtons'
import type { ExportDoc, ExportRow } from '../export/model'
import { GridChrome, ResizeHandle, useGridLayout } from './gridLayout'
import { SortHeader, compareBy, useGridSort, useSortMirror } from './gridSort'
import type { ReactNode } from 'react'

/** Names this grid's column layout for the session. */
const GRID_ID = 'receivables-dso'

/** The totals the footer and the extract both read. */
interface DsoTotals {
  receivables: number | null
  sales365: number | null
  dso: number | null
  masked: boolean
  /** The new permission model answered (M8). */
  v2: boolean
  /** Under it, the totals that leave a withheld or partly withheld row out. */
  partial: ReadonlySet<string>
}

/** A measure under the new model: blank is withheld, and Partial's name marks a partly withheld one (M8). */
const figure = (n: DsoNode, v: number | null, name: string, v2: boolean): ReactNode =>
  v === null ? (v2 ? <Withheld /> : '—') : markPartial(partialOf(n).has(name), money(v))
const footFigure = (t: DsoTotals, v: number | null, name: string): ReactNode =>
  v === null ? (t.v2 ? <Withheld /> : '—') : markPartial(t.partial.has(name), money(v))

/**
 * THE MEASURE COLUMNS, and why the member column is not one of them.
 *
 * The first column is the drill handle: it carries the chevron, the button that
 * descends a level, and a header whose text changes as you go ("Company" then
 * "Business Unit" then "Salesperson"). Hiding it would leave a grid of numbers
 * addressed to nothing, moving it would break the reading, and it has no stable
 * header for an extract to match on. So it stays fixed furniture — always
 * first — and the reader owns the three measures beside it.
 */
interface Measure {
  key: string
  header: string
  width: number
  kind: 'money' | 'num2'
  cellClass: (n: DsoNode) => string
  cell: (n: DsoNode, v2: boolean) => ReactNode
  value: (n: DsoNode) => number | null
  footClass: (t: DsoTotals) => string
  foot: (t: DsoTotals) => ReactNode
  footValue: (t: DsoTotals) => number | null
}

const MEASURES: Measure[] = [
  {
    key: 'totalReceivables', header: 'Total Receivables', width: 192, kind: 'money',
    cellClass: () => 'text-(--color-ink)',
    cell: (n, v2) => figure(n, n.receivables, 'receivables', v2),
    value: (n) => n.receivables,
    footClass: () => 'text-(--color-ink)',
    foot: (t) => footFigure(t, t.receivables, 'receivables'),
    footValue: (t) => t.receivables,
  },
  {
    key: 'sales365', header: '365 Days Sales', width: 192, kind: 'money',
    cellClass: () => 'text-(--color-ink-2)',
    cell: (n, v2) => figure(n, n.sales365, 'sales365', v2),
    value: (n) => n.sales365,
    footClass: () => 'text-(--color-ink)',
    foot: (t) => footFigure(t, t.sales365, 'sales365'),
    footValue: (t) => t.sales365,
  },
  {
    key: 'dso', header: 'DSO (days)', width: 128, kind: 'num2',
    cellClass: (n) => dsoTone(n.dso),
    /* NOT "0". An undefined ratio shown as zero is the healthiest number on the
       page, awarded to the least collectable row. */
    cell: (n, v2) =>
      // Under the new model a blank ratio is withheld when either side of it is.
      // Under the new model a blank ratio is withheld: 055 gives 0 where there are no sales.
      n.dso === null && v2 ? (
        <Withheld />
      ) : n.dso === null ? (
        <span title="No sales in the period, so days outstanding cannot be calculated">no sales</span>
      ) : (
        markPartial(partialOf(n).has('dso'), n.dso.toFixed(2))
      ),
    value: (n) => n.dso,
    footClass: (t) => dsoTone(t.dso),
    /* The ratio of the SUMS, not the mean of the column above it. */
    foot: (t) => (t.masked ? (t.v2 ? <Withheld /> : '—') : t.dso === null ? 'no sales' : markPartial(t.partial.has('dso'), t.dso.toFixed(2))),
    footValue: (t) => t.dso,
  },
]

/**
 * The levels the crumb-to-query loop in `load` can actually express.
 * Salesperson is deliberately absent: it is the leaf, and nothing below it.
 */
const DRILLABLE_LEVELS = new Set(['Company', 'BusinessUnit', 'BusinessTeam'])

/**
 * A row is drillable only if the crumb it would append ADVANCES the query.
 *
 * REACHED: a blank node key. The client drops it (`if (options.company)` is a
 * truthiness test) and the API trims it to NULL, so the "child" request is
 * byte-identical to the parent's and the same rows come back — with hasChildren
 * still true, because the server derives it from the LEVEL alone (014:255) and
 * never inspects the key. The row then stays clickable forever while the grid
 * never changes, and each click appends another identical breadcrumb. There are
 * 8 blank-BU rows in the live data, so this is not theoretical.
 *
 * BELT AND BRACES: the DRILLABLE_LEVELS test. The server cannot currently emit
 * an expandable Salesperson node (014:255 gives HasChildren = 0 at @Level 4), so
 * this hides nothing today — it is here so the client does not depend on that
 * staying true, since `load` can only express Company/BU/BT.
 *
 * Same defect family as the ReceivablesMatrix walk that blanked the report: an
 * identity builder that is not guaranteed to advance. Treat it as the leaf it is.
 */
function canDrill(n: DsoNode): boolean {
  // `?.trim()`, not just `!!`: the API normalises with IsNullOrWhiteSpace, so a
  // whitespace-only key is TRUTHY here but arrives NULL at the procedure — the
  // same non-advancing drill by another route. Matches explorerDrillable() in
  // SalesPage, which got this right; these two did not, in the same commit.
  return n.hasChildren && !!n.nodeKey?.trim() && DRILLABLE_LEVELS.has(n.groupedBy)
}

/**
 * SALESPERSON DSO — days sales outstanding, drilled Company → BU → BT → Person.
 *
 * Structure taken from the deployed Fabric app's DsoMatrix; the data path is
 * ours, so every level is scoped, masked and audited.
 *
 * ---------------------------------------------------------------------------
 * THE ONE THING TO GET RIGHT ON THIS SCREEN
 *
 * A NULL DSO is not zero days. It means there were no sales to divide by, so
 * the ratio is undefined. Zero is the BEST possible days-outstanding, so
 * rendering an undefined ratio as 0 puts the least collectable salesperson at
 * the top of the healthy list — which is what the app being replaced does.
 *
 * Here it renders as "no sales" in muted type, and the row is still shown with
 * its receivable, because a salesperson holding money against no sales is
 * exactly who a credit controller wants to see.
 */

/** Days-outstanding bands. Colour is the only thing carrying meaning here. */
function dsoTone(d: number | null): string {
  if (d === null) return 'text-(--color-ink-4)'
  if (d >= 180) return 'text-(--color-overdue) font-semibold'
  if (d >= 90) return 'text-(--color-warn) font-semibold'
  return 'text-(--color-ink)'
}

const LEVEL_CAPTION: Record<DsoNode['groupedBy'], string> = {
  Company: 'Company',
  BusinessUnit: 'Business Unit',
  BusinessTeam: 'Business Team',
  Salesperson: 'Salesperson',
}

interface Crumb {
  level: DsoNode['groupedBy']
  value: string
}

export function DsoTable({ api, viewAs = null, filters = NO_RECV_FILTERS }: {
  api: Api
  viewAs?: string | null
  /** FOUR of the bar's twelve reach here. dbo.D365_Salesperson_DSO has nine
      columns and no customer, document, aging period, payment terms or region,
      so the page draws only company, unit, team and salesperson on this tab. */
  filters?: RecvFilters
}) {
  const [path, setPath] = useState<Crumb[]>([])
  const [nodes, setNodes] = useState<DsoNode[] | null>(null)
  /** The new permission model answered (M8). */
  const [v2, setV2] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [loading, setLoading] = useState(true)

  // Whose eyes we look through resets the drill path — DURING RENDER, not in
  // an effect. An effect-based reset always lands after the load effect, so it
  // (a) fired a second, identical query on every mount, because setPath([])
  // with a fresh array is a state change, and (b) on a View-As switch sent
  // the NEW identity a query carrying the OLD identity's full drill path
  // before the reset caught up. Resetting here, the load effect below sees an
  // empty path in the same pass, and there is exactly one query either way.
  const [pathOwner, setPathOwner] = useState(viewAs)
  /* THE FILTER BAR RESETS THE DRILL PATH TOO, and for the same reason viewAs
     does: a crumb naming a company the bar now excludes is a heading over rows
     that cannot exist. Compared by reference, which is why the page keeps the
     filters in state and replaces the object on every change. */
  const [filterOwner, setFilterOwner] = useState(filters)
  const memberRef = useRef<HTMLTableCellElement | null>(null)
  const layout = useGridLayout(GRID_ID, MEASURES, memberRef)
  const { sort: sortState, setSort, toggle: toggleSort, ariaSort } = useGridSort()
  /* THE SORT RIDES INSIDE A SAVED VIEW. An unrecognised key -- a column since
     dropped, or one this reader may not be shown -- falls back to this grid's
     own order rather than sorting by nothing. See useSortMirror. */
  useSortMirror(GRID_ID, sortState, (s) => {
    const known = s.key === 'member' || MEASURES.some((m) => m.key === s.key)
    setSort(known ? s : { key: null, desc: true })
  })
  const shown = layout.columns
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)
  if (viewAs !== pathOwner || filters !== filterOwner) {
    setPathOwner(viewAs)
    setFilterOwner(filters)
    setPath([])
  }

  // Only the NEWEST request may write state. `nodes` and `path` are separate
  // state, and the next drill mints its crumb from the RENDERED node's level
  // (not from the path), so a stale response landing after a newer one would
  // put the grid at one level under crumbs at another, and the following
  // click would append a crumb carrying the wrong level. A ref counter rather
  // than an effect-cleanup flag because `load` is also called by the retry
  // button, outside any effect.
  const seq = useRef(0)

  const load = useCallback(async () => {
    const mine = ++seq.current
    setLoading(true)
    setFailure(null)
    try {
      const q: { company?: string; bu?: string; bt?: string; viewAs?: string; filters?: RecvFilters } = {
        filters,
      }
      for (const c of path) {
        if (c.level === 'Company') q.company = c.value
        else if (c.level === 'BusinessUnit') q.bu = c.value
        else if (c.level === 'BusinessTeam') q.bt = c.value
      }
      const { data: rows, vis } = await api.receivablesDso(q)
      if (mine !== seq.current) return // superseded — a newer request owns the screen
      setNodes(rows)
      setV2(vis !== null)
    } catch (e: unknown) {
      if (mine !== seq.current) return
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
    } finally {
      if (mine === seq.current) setLoading(false)
    }
  }, [api, path, filters, viewAs])

  useEffect(() => {
    void load()
  }, [load])

  if (failure) return <FailureState failure={failure} onRetry={() => void load()} />

  const caption = nodes && nodes.length > 0 ? LEVEL_CAPTION[nodes[0].groupedBy] : 'Company'

  /* SORTING — client-side, and honestly so: this table holds every row of the
     level it is showing (each drill fetches its own level, nothing is paged),
     so reordering in the browser reorders the whole answer.

     The member column sorts by name; the three measures sort by the same
     accessor the cell renders from, so the order always matches what is on
     screen. `no sales` and masked figures are null and land last either way —
     the comparator's rule, and the reason it is worth having: an undefined
     ratio is not the best DSO on the page. */
  const ordered = (() => {
    const list = nodes ?? []
    if (!sortState.key) return list
    if (sortState.key === 'member') {
      return [...list].sort((a, b) => compareBy(a.nodeKey ?? '', b.nodeKey ?? '', sortState.desc))
    }
    const m = MEASURES.find((x) => x.key === sortState.key)
    if (!m) return list
    return [...list].sort((a, b) =>
      compareBy(m.value(a), m.value(b), sortState.desc, () =>
        (a.nodeKey ?? '').localeCompare(b.nodeKey ?? ''),
      ),
    )
  })()
  /* Null if ANY row is masked — the total is then genuinely unknown, not zero.
     The house rule, as in ReceivablesMatrix and KpiStrip: `?? 0` inside a sum
     turned a masked column into a footer that read 0 under a column of em
     dashes, and 0 receivables is a figure a finance user acts on. */
  const sum = (pick: (n: DsoNode) => number | null): number | null => {
    const rows = nodes ?? []
    if (rows.some((n) => pick(n) === null)) return null
    return rows.reduce((s, n) => s + (pick(n) ?? 0), 0)
  }
  const totalRec = sum((n) => n.receivables)
  const totalSales = sum((n) => n.sales365)
  /* Two different nulls: masked (either sum unknown) versus no sales to divide
     by. The footer says which; the export carries null for both, which its
     model renders as an em dash — never a zero. */
  const totalMasked = totalRec === null || totalSales === null
  const totalDso = totalMasked || !totalSales ? null : (totalRec / totalSales) * 365
  /* UNDER THE NEW MODEL a row can show its receivables and withhold its sales, or
     the reverse, one piece of access at a time (M8). Each sum is what shows,
     marked when it leaves anything out; the ratio is taken over the rows that
     show both, so its two halves describe the same rows, and is marked when that
     leaves any row out. */
  const totals: DsoTotals = v2
    ? (() => {
        const rows = nodes ?? []
        const rec = sumShown(rows, ['receivables', (n) => n.receivables])
        const sales = sumShown(rows, ['sales365', (n) => n.sales365])
        // Rows whose receivables and sales both come whole from the same lines, so
        // their ratio is the server's (16c's review): a row that leaves either out
        // would put receivables over sales from different lines.
        const both = rows.filter((n) => n.dso !== null && !['receivables', 'sales365', 'dso'].some((k) => partialOf(n).has(k)))
        const bRec = both.reduce((s, n) => s + (n.receivables ?? 0), 0)
        const bSales = both.reduce((s, n) => s + (n.sales365 ?? 0), 0)
        const partial = new Set<string>()
        if (rec.partial) partial.add('receivables')
        if (sales.partial) partial.add('sales365')
        if (both.length > 0 && both.length < rows.length) partial.add('dso')
        return {
          receivables: rec.value, sales365: sales.value, masked: both.length === 0,
          dso: both.length > 0 ? (bSales ? (bRec / bSales) * 365 : 0) : null, v2: true, partial,
        }
      })()
    : { receivables: totalRec, sales365: totalSales, dso: totalDso, masked: totalMasked, v2: false, partial: new Set<string>() }
  /** An extract's figure: a blank one is withheld under the new model, except a ratio over no sales. */
  const exportFigure = (c: Measure, n: DsoNode): string | number | null => {
    const v = c.value(n)
    if (!v2 || v !== null) return v
    return WITHHELD
  }

  /* THE EXTRACT — the level in front of the reader, with the drill path stated
     as context. A null DSO stays null: no sales, so the ratio is undefined —
     an em dash in the file, never zero (see the note at the top of this file). */
  const buildDoc = (): ExportDoc | null => {
    if (loading || !nodes || nodes.length === 0) return null
    return {
      report: 'Receivables',
      page: 'Salesperson DSO',
      title: 'Days sales outstanding',
      subtitle: `By ${caption.toLowerCase()} — receivables ÷ 365-day sales × 365, computed on the rolled-up sums.`,
      context: [
        `Path: ${['All companies', ...path.map((c) => c.value || '(blank)')].join(' › ')}`,
        'Amounts in local currency; the ratio is the same in any.',
      ],
      privileged: viewAs,
      currency: LOCAL,
      tables: [
        {
          /* The extract is the screen: the member column always, then whichever
             measures the reader has left showing, in their order. */
          columns: [{ header: caption }, ...shown.map((c) => ({ header: c.header, kind: c.kind }))],
          rows: [
            ...ordered.map<ExportRow>((n) => ({
              cells: [memberText(n.nodeKey, partialOf(n).has('nodeKey'), caption), ...shown.map((c) => exportFigure(c, n))],
            })),
            { bold: true, cells: ['Total', ...shown.map((c) => {
              const v = c.footValue(totals)
              return v === null && v2 && (c.key !== 'dso' || totals.masked) ? WITHHELD : v
            })] } satisfies ExportRow,
          ],
          note: 'A dash in DSO means no sales in the period — the ratio is undefined, not zero. The total DSO is the ratio of the sums, not the mean of the column above it.'
            + (v2 && (totals.partial.size > 0 || ordered.some((n) => partialOf(n).size > 0))
              ? ` Under your access the total DSO is over the rows that show both receivables and sales. ${PARTIAL_NOTE}`
              : ''),
        },
      ],
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Breadcrumbs, same pattern as the tree so the two pages navigate alike;
          the extract pair shares the row so neither costs the grid any height. */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pb-3 pt-4">
      <nav aria-label="DSO path" className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => setPath([])}
          className={`rounded px-2.5 py-1 text-(length:--text-small) font-medium transition-colors ${
            path.length === 0
              ? 'bg-(--color-navy-700) text-white'
              : 'bg-(--color-surface-3) text-(--color-ink-2) hover:bg-(--color-navy-100)'
          }`}
        >
          All companies
        </button>
        {/* Position in the key, not just level+value: the crumb stack is only
            ever appended to or truncated to a prefix, so the index is stable —
            and without it two crumbs carrying the same value collide, which is
            the duplicate-key condition that corrupts reconciliation. */}
        {path.map((c, i) => (
          <span key={`${i}-${c.level}-${c.value}`} className="flex items-center gap-1.5">
            <span aria-hidden className="text-(--color-ink-4)">›</span>
            <button
              type="button"
              onClick={() => setPath(path.slice(0, i + 1))}
              className={`max-w-[220px] truncate rounded px-2.5 py-1 text-(length:--text-small) font-medium transition-colors ${
                i === path.length - 1
                  ? 'bg-(--color-navy-700) text-white'
                  : 'bg-(--color-surface-3) text-(--color-ink-2) hover:bg-(--color-navy-100)'
              }`}
            >
              {c.value || '(blank)'}
            </button>
          </span>
        ))}
      </nav>
      <div className="flex items-center gap-2">
        <ExportButtons build={buildDoc} />
        <GridChrome api={layout} />
      </div>
      </div>

      <div className="scroll-slim min-h-0 flex-1 overflow-auto">
        <table className="report-table w-full min-w-max border-separate border-spacing-0 text-(length:--text-body)">
          <thead>
            <tr>
              {/* The drill column is fixed furniture: always first, always on
                  screen, never in the layout. Sticky so that a measure the
                  reader pins beside it has something to sit against — the hook
                  measures this cell to offset the pins past it. */}
              <th
                ref={memberRef}
                scope="col"
                aria-sort={ariaSort('member')}
                className="sticky left-0 top-0 z-30 border-y border-(--color-line) px-5 py-2.5 text-center font-semibold text-(--color-ink)"
              >
                {/* The drill column sorts too — by name. It is the one column
                    a reader looking for a particular salesperson wants
                    alphabetical, and it costs nothing: the chevron lives in
                    the body cells, not here. */}
                <SortHeader
                  label={caption}
                  active={sortState.key === 'member'}
                  desc={sortState.desc}
                  onToggle={() => toggleSort('member')}
                  surface="dso"
                  measureKey="member"
                />
              </th>
              {shown.map((c) => {
                const pin = layout.pinOf(c.key)
                const w = layout.widthOf(c.key) ?? c.width
                return (
                  <th
                    key={c.key}
                    ref={layout.headerRef(c.key)}
                    scope="col"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move'
                      setDragKey(c.key)
                    }}
                    onDragOver={(e) => {
                      if (!dragKey || dragKey === c.key) return
                      e.preventDefault()
                      setOverKey(c.key)
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      if (dragKey && dragKey !== c.key) layout.move(dragKey, layout.indexOf(c.key))
                      setDragKey(null)
                      setOverKey(null)
                    }}
                    onDragEnd={() => { setDragKey(null); setOverKey(null) }}
                    style={{
                      width: w, minWidth: w, // a floor, not a cap: every column here is a figure (4 Oct 2026)
                      left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                      right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                    }}
                    className={`relative sticky top-0 border-y border-l border-(--color-line) px-4 py-2.5 text-center font-semibold text-(--color-ink) ${
                      pin ? 'z-30' : 'z-20'
                    } ${overKey === c.key ? 'bg-(--color-navy-100)' : ''} ${dragKey === c.key ? 'opacity-50' : ''}`}
                    title={c.key === 'dso' ? 'Receivables ÷ 365-day sales × 365' : undefined}
                    aria-sort={ariaSort(c.key)}
                  >
                    <SortHeader
                      label={c.header}
                      active={sortState.key === c.key}
                      desc={sortState.desc}
                      onToggle={() => toggleSort(c.key)}
                      surface="dso"
                      measureKey={c.key}
                    />
                    <ResizeHandle
                      label={c.header}
                      widthOf={() => layout.headerWidth(c.key)}
                      minWidth={() => layout.minWidthOf(c.key)}
                      onResize={(px) => layout.resize(c.key, px)}
                      onAutoFit={() => layout.autoFit(c.key)}
                    />
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {ordered.map((n) => (
              <tr
                key={n.nodeKey ?? '(blank)'}
                className="group transition-colors hover:bg-(--color-navy-50)"
              >
                <th
                  scope="row"
                  className="sticky left-0 z-[2] border-b border-(--color-line) bg-(--color-surface) px-5 py-2 text-left font-medium text-(--color-ink) group-hover:bg-(--color-navy-50)"
                >
                  {canDrill(n) ? (
                    <button
                      type="button"
                      onClick={() => setPath([...path, { level: n.groupedBy, value: n.nodeKey ?? '' }])}
                      className="flex items-center gap-1.5 text-left transition-colors hover:text-(--color-accent)"
                    >
                      <span aria-hidden className="text-[10px] text-(--color-ink-4)">▶</span>
                      {memberName(n.nodeKey, partialOf(n).has('nodeKey'), LEVEL_CAPTION[n.groupedBy])}
                    </button>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <span aria-hidden className="inline-block w-3" />
                      {memberName(n.nodeKey, partialOf(n).has('nodeKey'), LEVEL_CAPTION[n.groupedBy])}
                    </span>
                  )}
                </th>
                {shown.map((c) => {
                  const pin = layout.pinOf(c.key)
                  const w = layout.widthOf(c.key) ?? c.width
                  return (
                    <td
                      key={c.key}
                      style={{
                        width: w,
                        left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                        right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                      }}
                      /* border-B as well as border-L: the row line lives on the
                         CELL since this table became border-separate, and the
                         measure cells were left without one — the identity
                         column beside them had it, so the rows read as
                         separated on the left and merged on the right. */
                      className={`num whitespace-nowrap border-b border-l border-(--color-line) px-4 py-2 ${c.cellClass(n)} ${
                        pin ? 'sticky z-[1] bg-(--color-surface) group-hover:bg-(--color-navy-50)' : ''
                      }`}
                    >
                      {c.cell(n, v2)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>

          {nodes && nodes.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-(--color-line-strong) bg-(--color-surface-3) font-semibold">
                <th scope="row" className="sticky left-0 z-[3] bg-(--color-surface-3) px-5 py-2.5 text-left text-(--color-ink)">
                  Total
                </th>
                {shown.map((c) => {
                  const pin = layout.pinOf(c.key)
                  const w = layout.widthOf(c.key) ?? c.width
                  return (
                    <td
                      key={c.key}
                      style={{
                        width: w,
                        left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                        right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                      }}
                      className={`num border-l border-(--color-line) px-4 py-2.5 ${c.footClass(totals)} ${
                        pin ? 'sticky z-[2] bg-(--color-surface-3)' : ''
                      }`}
                    >
                      {c.foot(totals)}
                    </td>
                  )
                })}
              </tr>
            </tfoot>
          )}
        </table>

        {/* On EVERY fetch: expanding a node refetches, and until now that showed
            nothing at all while the request was in flight. */}
        {loading && <Loading label="Loading DSO…" />}
        {!loading && nodes && nodes.length === 0 && (
          <EmptyState
            title={viewAs ? `${viewAs} has nothing here` : 'Nothing to show'}
            detail="No salespeople with receivables in this branch. This is an empty result, not a refusal."
          />
        )}
      </div>
    </div>
  )
}
